import { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import app from "../firebase/config"; // abc-dashboard's own Firebase app instance
import { useAuth } from "../context/AuthContext";
import { fmtDT } from "../crm/dateFormat";

// ── DPDP Switch ───────────────────────────────────────────────────
// Owner-only. Turns the DPDP parts of the live app (consent screen,
// My Privacy, Privacy Requests) On or Off. The change is made by a
// staff-only Cloud Function (functions/dpdpSwitch.js) — never by
// editing the database by hand. If no setting exists, the live app
// treats DPDP as ON.

const OWNER_EMAIL = "layakarudu@gmail.com";

const functions = getFunctions(app, "asia-south1");
const getDpdpSwitchFn = httpsCallable(functions, "getDpdpSwitch");
const setDpdpSwitchFn = httpsCallable(functions, "setDpdpSwitch");

const S = {
  page: { padding: "24px", maxWidth: "640px", margin: "0 auto", color: "#e8e8f0" },
  title: { fontSize: "22px", fontWeight: "700", marginBottom: "4px" },
  sub: { fontSize: "13px", color: "#8888a8", marginBottom: "24px" },
  card: { background: "#1b1d27", border: "1px solid #2e2e40", borderRadius: "12px", padding: "18px", marginBottom: "16px" },
  status: { fontSize: "13px", marginTop: "12px" },
  btn: { padding: "12px 20px", borderRadius: "8px", border: "none", color: "#fff", cursor: "pointer", fontSize: "14px", fontWeight: "700", marginTop: "14px" },
};

export default function DpdpSwitchScreen() {
  const { user } = useAuth();
  const isOwner = (user?.email || "").trim().toLowerCase() === OWNER_EMAIL;

  const [state, setState] = useState(null); // { enabled, updatedAt, updatedBy, isDefault }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!isOwner) { setLoading(false); return; }
    (async () => {
      try {
        const res = await getDpdpSwitchFn();
        setState(res.data);
      } catch (e) {
        console.error("Load DPDP switch failed:", e);
        setMsg("Couldn't read the current setting. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [isOwner]);

  async function change(nextEnabled) {
    const question = nextEnabled
      ? "Turn DPDP features ON for the live app?"
      : "Turn DPDP features OFF for the live app?\n\nUsers will stop seeing the consent screen, My Privacy and Privacy Requests.";
    if (!window.confirm(question)) return;

    setSaving(true);
    setMsg("");
    try {
      const res = await setDpdpSwitchFn({ enabled: nextEnabled });
      setState(res.data);
      setMsg(nextEnabled ? "✓ DPDP features are now ON." : "✓ DPDP features are now OFF.");
    } catch (e) {
      console.error("Change DPDP switch failed:", e);
      setMsg("Couldn't change the setting: " + (e.message || "Unknown error."));
    } finally {
      setSaving(false);
    }
  }

  if (!isOwner) {
    return (
      <div style={S.page}>
        <div style={S.card}>Only the Owner can view the DPDP switch.</div>
      </div>
    );
  }

  if (loading) {
    return <div style={S.page}>Loading current setting…</div>;
  }

  const on = state ? state.enabled : true;

  return (
    <div style={S.page}>
      <div style={S.title}>🛡 DPDP Switch</div>
      <div style={S.sub}>
        Controls the DPDP parts of the live app: the consent screen, "My Privacy" and the committee's
        "Privacy Requests" card. The live app picks up a change the next time a person opens or refreshes it.
      </div>

      <div style={S.card}>
        <div style={{ fontSize: "13px", color: "#8888a8" }}>DPDP features are currently</div>
        <div style={{ fontSize: "28px", fontWeight: "700", color: on ? "#4cd28a" : "#ff7b7b", margin: "6px 0" }}>
          {on ? "ON" : "OFF"}
        </div>

        <div style={{ fontSize: "12px", color: "#8888a8" }}>
          {state && state.isDefault
            ? "No change has been made yet (ON is the default)."
            : state && state.updatedAt
            ? `Last changed by ${state.updatedBy || "unknown"} on ${fmtDT(state.updatedAt)}`
            : ""}
        </div>

        <button
          style={{ ...S.btn, background: on ? "#8a3b3b" : "#2f7d55", opacity: saving ? 0.6 : 1 }}
          disabled={saving || !state}
          onClick={() => change(!on)}
        >
          {saving ? "Saving…" : on ? "Turn OFF" : "Turn ON"}
        </button>

        {msg && (
          <div style={{ ...S.status, color: msg.startsWith("✓") ? "#4cd28a" : "#ff7b7b" }}>{msg}</div>
        )}
      </div>

      <div style={{ ...S.card, fontSize: "12px", color: "#aab2c5" }}>
        Keep this ON. It must stay ON from 13 May 2027, and once the Android app is on Google Play.
        Use OFF only for the short test we are doing now.
      </div>
    </div>
  );
}